// =============================================================================
// NAVBAR COMPONENT
// =============================================================================

import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { ShoppingBagIcon } from '@/components/ShoppingBag';
import { analytics } from '@/services/analytics';
import type { NavItem } from '@/types';

interface NavbarProps {
  brand?: string;
  navItems?: NavItem[];
  showThemeSwitcher?: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({
  brand = 'Awani',
  navItems = [],
  showThemeSwitcher = true,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    const action = !isMobileMenuOpen ? 'open' : 'close';
    analytics.trackMobileMenuToggle(action);
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (item: NavItem, source: 'desktop' | 'mobile') => {
    analytics.trackNavigationClick(item.href, source === 'mobile' ? 'mobile_menu' : 'navbar');
    if (item.external) {
      analytics.trackOutboundLink(item.href, item.label);
    }
    if (source === 'mobile') {
      closeMobileMenu();
    }
  };

  return (
    <nav className="navbar">
      <div className="container">
        <div className="navbar-content">
          <a href="/" className="nav-brand">
            <img 
              src="/images/logo.png" 
              alt={`${brand} logo`}
              className="nav-logo"
              width="32"
              height="32"
            />
            <span className="brand-text">{brand}</span>
          </a>

          <div className="nav-links">
            {navItems.map((item, index) => (
              <a
                key={index}
                href={item.href}
                className="nav-link"
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer' : undefined}
                onClick={() => handleNavClick(item, 'desktop')}
              >
                {item.label}
              </a>
            ))}
          </div>

          <div className="navbar-actions">
            {/* {showThemeSwitcher && <ThemeSwitcher />} */}
            <ShoppingBagIcon />
            
            <button
              onClick={toggleMobileMenu}
              className="nav-toggle"
              aria-label="Toggle mobile menu"
              type="button"
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        <div className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
          {navItems.map((item, index) => (
            <a
              key={index}
              href={item.href}
              className="mobile-nav-link"
              target={item.external ? '_blank' : undefined}
              rel={item.external ? 'noopener noreferrer' : undefined}
              onClick={() => handleNavClick(item, 'mobile')}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
};
